import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import YouTubeLite from "@/components/sections/YouTubeLite";

// Basking shark filmed from the shoreline of Whale Cove — the site's strongest
// public proof-of-concept (180k+ views on YouTube within a week of posting).
const BASKING_SHARK_YT_ID = "NQFKUo6KF8I";

export default function BaskingShark() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section
      ref={ref}
      id="basking-shark"
      className="relative wc-section bg-[#2C3828] text-wc-cream overflow-hidden"
      data-testid="basking-shark-section"
    >
      {/* Subtle grain / noise for depth on the dark bg */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
      />

      <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-14 items-center">
        {/* Vertical Short video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="w-full md:w-[280px] mx-auto md:mx-0"
        >
          <div className="relative rounded-sm overflow-hidden shadow-2xl border border-wc-teal/20">
            <YouTubeLite
              youtubeId={BASKING_SHARK_YT_ID}
              title="Basking shark filmed from the shoreline at Whale Cove Off Grid — Bay of Fundy, Nova Scotia"
              aspectClass="aspect-[9/16]"
              autoplay={true}
            />
          </div>
          <p className="font-accent italic text-wc-driftwood/60 text-xs text-center mt-3 tracking-wide">
            Tap to watch · YouTube Short
          </p>
        </motion.div>

        {/* Editorial text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <div className="inline-flex items-center gap-2 text-wc-teal font-accent italic text-sm mb-4 uppercase tracking-[0.2em]">
            <span className="w-8 h-px bg-wc-teal" />
            180,000+ views · one week old
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
            A basking shark, straight from the shore.
          </h2>
          <p className="font-body text-wc-driftwood/90 text-base sm:text-lg leading-relaxed mb-5">
            One afternoon this month, we filmed a basking shark — the second-largest fish on Earth — moving through the shallows of Whale Cove. We didn't leave the property. We didn't need a boat.
          </p>
          <p className="font-body text-wc-driftwood/90 text-base sm:text-lg leading-relaxed mb-8">
            180,000 people have watched it since. It's the clearest answer we can offer to the question <em className="text-wc-cream not-italic font-medium">"can you really see wildlife from land here?"</em>
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/whales"
              data-testid="basking-shark-whales-cta"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-wc-teal text-wc-forest font-body font-semibold rounded-sm hover:bg-wc-cream transition-colors"
            >
              <Play className="w-4 h-4" />
              More from the water
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={`https://youtube.com/shorts/${BASKING_SHARK_YT_ID}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="basking-shark-yt-link"
              className="font-body text-wc-driftwood/80 text-sm underline underline-offset-4 decoration-wc-teal/40 hover:text-wc-cream transition-colors"
            >
              Watch on YouTube
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
